import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Video, Square, Upload, Trash2, Loader2, RefreshCcw, Circle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 2 * 1024 * 1024;
const MAX_SECONDS = 90;

const mediaUrl = (u) => {
  if (!u) return '';
  let x = u;
  if (x.startsWith('/uploads/')) x = `/api${x}`;
  if (x.startsWith('/')) x = `${API_URL}${x}`;
  return x;
};

export default function IntroVideoManager() {
  const { user, token } = useAuth();
  const [currentUrl, setCurrentUrl] = useState('');
  const [mode, setMode] = useState('idle'); // idle | recording | preview
  const [seconds, setSeconds] = useState(0);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const liveRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API_URL}/api/members/${user.id}/profile`)
      .then((res) => setCurrentUrl(res.data?.user?.microsite_intro_video_url || ''))
      .catch(() => {});
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const stopStream = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      setMode('recording');
      setSeconds(0);
      setTimeout(() => {
        if (liveRef.current) {
          liveRef.current.srcObject = stream;
          liveRef.current.play().catch(() => {});
        }
      }, 50);

      const mime = ['video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4']
        .find((m) => MediaRecorder.isTypeSupported(m)) || '';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mime.includes('mp4') ? 'video/mp4' : 'video/webm' });
        setPreviewBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        setMode('preview');
        stopStream();
      };
      recorderRef.current = recorder;
      recorder.start(1000);

      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) stopRecording();
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      toast.error('Camera access denied. Allow camera & microphone to record.');
      setMode('idle');
      stopStream();
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const uploadBlob = async (blob, filename) => {
    setUploading(true);
    setProgress(0);
    try {
      const startRes = await axios.post(
        `${API_URL}/api/users/me/microsite/intro-video/start`,
        { filename, file_size: blob.size },
        { headers: authHeaders },
      );
      const uploadId = startRes.data.upload_id;
      const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const fd = new FormData();
        fd.append('chunk_index', i);
        fd.append('total_chunks', totalChunks);
        fd.append('chunk', blob.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await axios.post(
          `${API_URL}/api/users/me/microsite/intro-video/${uploadId}/chunk`,
          fd,
          { headers: authHeaders },
        );
        setProgress(Math.round(((i + 1) / totalChunks) * 90));
      }
      const done = await axios.post(
        `${API_URL}/api/users/me/microsite/intro-video/${uploadId}/complete`,
        {},
        { headers: authHeaders },
      );
      setProgress(100);
      setCurrentUrl(done.data.intro_video_url);
      setMode('idle');
      setPreviewBlob(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
      toast.success('Intro video is live on your micro-site!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100MB'); return; }
    if (!/\.(mp4|webm|mov|m4v)$/i.test(file.name)) { toast.error('Use an mp4, webm, or mov file'); return; }
    uploadBlob(file, file.name);
    e.target.value = '';
  };

  const removeIntro = async () => {
    try {
      await axios.delete(`${API_URL}/api/users/me/microsite/intro-video`, { headers: authHeaders });
      setCurrentUrl('');
      toast.success('Intro video removed');
    } catch {
      toast.error('Could not remove video');
    }
  };

  const discardPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setPreviewBlob(null);
    setMode('idle');
  };

  return (
    <div className="rounded-2xl bg-brand-surface border border-white/5 p-6 space-y-5" data-testid="intro-video-manager">
      <div className="flex items-start gap-3">
        <Video className="text-brand-gold flex-shrink-0" size={22} />
        <div>
          <h3 className="text-lg font-semibold text-white">Video intro</h3>
          <p className="text-xs text-slate-400">
            Record a short intro (up to {MAX_SECONDS}s) or upload a video — visitors see a
            <span className="text-brand-gold"> “Watch my intro”</span> button on your micro-site hero.
          </p>
        </div>
      </div>

      {/* Current intro */}
      {currentUrl && mode === 'idle' && !uploading && (
        <div className="rounded-xl overflow-hidden border border-white/10">
          <video src={mediaUrl(currentUrl)} controls playsInline className="w-full aspect-video bg-black" data-testid="intro-video-current" />
          <div className="flex items-center justify-between px-4 py-2 bg-black/30">
            <span className="text-xs text-emerald-400 font-medium">Live on your micro-site</span>
            <button onClick={removeIntro} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="intro-video-delete">
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>
      )}

      {/* Live recording */}
      {mode === 'recording' && (
        <div className="rounded-xl overflow-hidden border border-red-500/40 relative">
          <video ref={liveRef} muted playsInline className="w-full aspect-video bg-black object-cover" />
          <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 bg-black/60 backdrop-blur rounded-full">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-mono text-white">{String(Math.floor(seconds / 60)).padStart(1, '0')}:{String(seconds % 60).padStart(2, '0')} / 1:30</span>
          </div>
          <button
            onClick={stopRecording}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-xl"
            data-testid="intro-video-stop"
          >
            <Square className="w-5 h-5 text-white" fill="currentColor" />
          </button>
        </div>
      )}

      {/* Recorded preview */}
      {mode === 'preview' && previewUrl && (
        <div className="space-y-3">
          <video src={previewUrl} controls playsInline className="w-full aspect-video bg-black rounded-xl border border-white/10" data-testid="intro-video-preview" />
          <div className="flex items-center gap-2">
            <button
              onClick={() => uploadBlob(previewBlob, 'intro-recording.webm')}
              disabled={uploading}
              className="px-5 py-2 bg-brand-gold text-black font-semibold rounded-lg hover:brightness-110 disabled:opacity-50 flex items-center gap-2 text-sm"
              data-testid="intro-video-save"
            >
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              {uploading ? `Uploading ${progress}%` : 'Use this video'}
            </button>
            <button onClick={discardPreview} disabled={uploading} className="px-4 py-2 border border-white/10 rounded-lg text-sm text-slate-300 hover:bg-white/5 flex items-center gap-2">
              <RefreshCcw size={14} /> Re-record
            </button>
          </div>
        </div>
      )}

      {/* Upload progress (file path) */}
      {uploading && mode !== 'preview' && (
        <div className="rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-sm text-white mb-2">
            <Loader2 className="animate-spin text-brand-gold" size={16} /> Uploading… {progress}%
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-brand-gold transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Actions */}
      {mode === 'idle' && !uploading && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={startRecording}
            className="px-5 py-2 bg-brand-gold text-black font-semibold rounded-lg hover:brightness-110 flex items-center gap-2 text-sm"
            data-testid="intro-video-record"
          >
            <Circle size={14} fill="currentColor" className="text-red-600" /> Record with camera
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 border border-white/10 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-2"
            data-testid="intro-video-upload"
          >
            <Upload size={14} /> Upload a video file
          </button>
          <input ref={fileInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleFile} className="hidden" />
        </div>
      )}
    </div>
  );
}
